import React from "react";
import type { UseQueryResult } from "@tanstack/react-query";
import type { CommunityAlignmentRollup } from "lib/api/community";
import {
  formatCommunityAlignmentLabel,
  type CommunityUiStrings,
} from "i18n/communityUi";
import SectionShell from "../SectionShell";
import {
  SectionEmptyState,
  SectionErrorState,
  SectionLoadingState,
} from "../SectionStates";
import CommunityRadarChart from "../charts/CommunityRadarChart";
import { formatCommunityWindow } from "../utils";

interface CommunityAlignmentProps {
  query: UseQueryResult<CommunityAlignmentRollup>;
  labels: CommunityUiStrings;
  numberFormatter: Intl.NumberFormat;
}

/**
 * Alignment section pairing a radar chart with a dimension grid.
 */
const CommunityAlignment: React.FC<CommunityAlignmentProps> = ({
  query,
  labels,
  numberFormatter,
}) => {
  return (
    <SectionShell
      title={labels.alignmentTitle}
      subtitle={labels.alignmentSubtitle}
      contentClassName="space-y-5"
    >
      {query.isPending ? (
        <SectionLoadingState message={labels.loading} />
      ) : query.isError ? (
        <SectionErrorState
          message={labels.sectionError}
          retryLabel={labels.retry}
          onRetry={() => {
            void query.refetch();
          }}
          isRetrying={query.isRefetching}
        />
      ) : query.data && query.data.alignment_count > 0 ? (
        (() => {
          const summaryPoint = query.data.radar.find((point) => point.key === "summary");
          const score = summaryPoint?.value ?? 0;
          const description = `${labels.radarCaption} ${query.data.radar
            .map((point) => `${formatCommunityAlignmentLabel(point.key, labels)} ${point.value.toFixed(1)}`)
            .join(", ")}`;

          return (
            <div className="grid grid-cols-1 gap-6 xl:grid-cols-[1.1fr_1fr]">
              <div className="space-y-4 rounded-2xl border border-eoe-border/80 bg-eoe-bg-hover/25 p-4 dark:border-eoe-dark-border/80 dark:bg-eoe-dark-bg-hover/15">
                <div className="flex flex-wrap items-end gap-4">
                  <div>
                    <p className="text-sm font-medium text-eoe-text-sub dark:text-eoe-dark-text-sub">
                      {labels.alignmentOverallScore}
                    </p>
                    <p className="mt-2 text-4xl font-semibold tracking-tight text-eoe-text dark:text-eoe-dark-text">
                      {score.toFixed(1)}%
                    </p>
                  </div>
                  <div className="space-y-1 text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">
                    <p>{formatCommunityWindow(labels.windowDays, query.data.window_days)}</p>
                    <p>
                      {labels.alignmentResponses}: {numberFormatter.format(query.data.alignment_count)}
                    </p>
                  </div>
                </div>
                <CommunityRadarChart
                  points={query.data.radar}
                  title={labels.alignmentTitle}
                  description={description}
                />
              </div>
              <div className="space-y-3">
                {query.data.dimensions.map((dimension) => (
                  <div
                    key={dimension.dimension}
                    className="rounded-2xl border border-eoe-border/80 bg-eoe-bg-hover/25 p-4 dark:border-eoe-dark-border/80 dark:bg-eoe-dark-bg-hover/15"
                  >
                    <div className="mb-2 flex items-center justify-between gap-3">
                      <div>
                        <p className="font-medium text-eoe-text dark:text-eoe-dark-text">
                          {formatCommunityAlignmentLabel(dimension.dimension, labels)}
                        </p>
                        <p className="text-xs text-eoe-text-sub dark:text-eoe-dark-text-sub">
                          {numberFormatter.format(dimension.alignment_count)} {labels.alignmentDimensionParticipation}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-semibold text-eoe-text dark:text-eoe-dark-text">
                          {dimension.score.toFixed(1)}
                        </p>
                      </div>
                    </div>
                    <div className="h-2 overflow-hidden rounded-full bg-eoe-border/70 dark:bg-eoe-dark-border/70">
                      <div
                        className="h-full rounded-full bg-gradient-to-r from-blue-600 via-violet-500 to-emerald-500"
                        style={{ width: `${Math.round(dimension.score)}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })()
      ) : (
        <SectionEmptyState title={labels.emptyAlignment} />
      )}
    </SectionShell>
  );
};

export default CommunityAlignment;

